import { noop } from 'lodash';
import { ArdoqId, ViewIds } from '@ardoq/api-types';
import { type Action, dispatchAction } from '@ardoq/rxbeach';
import {
  updateViewContext as updateViewContextAction,
  updateViewProperties as updateViewPropertiesAction,
  updateViewSettings,
  type ViewContext,
} from '@ardoq/view-settings';
import { viewLegendCommands } from '@ardoq/view-legend';
import {
  focusedComponentChanged,
  hoveredComponentChanged,
} from 'tabview/actions';
import { componentSelectedInPresentationMode } from 'streams/components/ComponentActions';
import { setComponentId } from 'streams/context/ContextActions';
import {
  componentMatrixCollapseAllColumns,
  componentMatrixCollapseAllRows,
  componentMatrixExpandAllColumns,
  componentMatrixExpandAllRows,
  componentMatrixToggleCollapseGroup,
} from '../actions';
import {
  ComponentMatrix2Commands,
  ComponentMatrix2DataLayerCommands,
  ComponentMatrix2RenderingCommands,
  ComponentMatrix2ResizeCommands,
  ComponentMatrix2ViewSettings,
  ViewpointModeComponentMatrixViewModelProperties,
} from './types';

const VIEW_ID = ViewIds.COMPONENT_MATRIX_2;

const setContextComponentId = (componentId: ArdoqId) =>
  dispatchAction(setComponentId(componentId));

const updateViewContext = (viewContext: ViewContext) =>
  dispatchAction(updateViewContextAction(viewContext));

const updateViewProperties = (
  viewProperties: ViewpointModeComponentMatrixViewModelProperties
) =>
  dispatchAction(
    updateViewPropertiesAction({ viewId: VIEW_ID, viewProperties })
  );

const updateViewLegendSettings = (isActive: boolean) =>
  viewLegendCommands.updateViewLegendSettings({ viewId: VIEW_ID, isActive });

/**
 * The hover, focus and collapse commands, which turn each interaction into the
 * action the rendering data reducers handle. Shared by the connected view and
 * the data layer, so the mapping lives in one place.
 */
export const createComponentMatrix2RenderingCommands = (
  dispatchRenderingAction: (action: Action<unknown>) => void
): ComponentMatrix2RenderingCommands => ({
  onItemMouseEnter: itemId =>
    dispatchRenderingAction(hoveredComponentChanged(itemId)),
  onItemMouseLeave: () =>
    dispatchRenderingAction(hoveredComponentChanged(null)),
  onBackgroundClick: () =>
    dispatchRenderingAction(focusedComponentChanged(null)),
  onItemSelect: itemId =>
    dispatchRenderingAction(focusedComponentChanged(itemId)),
  onGroupExpanderClick: payload =>
    dispatchRenderingAction(componentMatrixToggleCollapseGroup(payload)),
  onCollapseAllRowsClick: () =>
    dispatchRenderingAction(componentMatrixCollapseAllRows()),
  onExpandAllRowsClick: () =>
    dispatchRenderingAction(componentMatrixExpandAllRows()),
  onCollapseAllColumnsClick: () =>
    dispatchRenderingAction(componentMatrixCollapseAllColumns()),
  onExpandAllColumnsClick: () =>
    dispatchRenderingAction(componentMatrixExpandAllColumns()),
});

/**
 * The resize commands, which turn each resize into the view settings it
 * changes. Shared by the connected view and the data layer, so the rules live
 * in one place.
 */
export const createComponentMatrix2ResizeCommands = (
  updateSettings: (settings: Partial<ComponentMatrix2ViewSettings>) => void
): ComponentMatrix2ResizeCommands => ({
  /**
   * Persist a user-adjusted column width into viewSettings (ARD-31416). Other
   * columns are preserved verbatim — the spec only lifts the auto-fit cap on
   * columns the user has actually touched. The view-model clamps the value to
   * the measured minimum so labels never truncate below their content.
   */
  onColumnResize: (currentWidths, columnKey, width) =>
    updateSettings({
      userAdjustedColumnWidths: { ...currentWidths, [columnKey]: width },
    }),
  /** Drop a single column from the user-adjusted map (double-click on handle). */
  onColumnResizeHandleDoubleClick: (currentWidths, columnKey) => {
    if (!(columnKey in currentWidths)) return;
    const next = { ...currentWidths };
    delete next[columnKey];
    updateSettings({ userAdjustedColumnWidths: next });
  },
  /**
   * Persist a user-adjusted row height into viewSettings (ARD-31416). Symmetric
   * to `onColumnResize`. The view-model clamps the value to the measured
   * minimum so wrapped labels and badges aren't clipped below their content
   * height; above the minimum the user value is used verbatim.
   */
  onRowResize: (currentHeights, rowKey, height) =>
    updateSettings({
      userAdjustedRowHeights: { ...currentHeights, [rowKey]: height },
    }),
  /** Drop a single row from the user-adjusted map (double-click on handle). */
  onRowResizeHandleDoubleClick: (currentHeights, rowKey) => {
    if (!(rowKey in currentHeights)) return;
    const next = { ...currentHeights };
    delete next[rowKey];
    updateSettings({ userAdjustedRowHeights: next });
  },
  /**
   * Persist the user-dragged width of the row-headers area (the leftmost
   * column, ARD-32764). The view-model clamps it to the level-headers-area
   * width so the axis label cards stay intact; above that the value is used
   * verbatim, with no upper cap so long labels can be revealed.
   */
  onRowHeadersResize: width =>
    updateSettings({ userAdjustedRowHeadersWidth: width }),
  /** Reset the row-headers area back to its auto content width (double-click). */
  onRowHeadersResizeHandleDoubleClick: () =>
    updateSettings({ userAdjustedRowHeadersWidth: undefined }),
});

const updatePersistentViewSettings = (
  settings: Partial<ComponentMatrix2ViewSettings>
) =>
  dispatchAction(
    updateViewSettings({ viewId: VIEW_ID, settings, persistent: true })
  );

export const COMPONENT_MATRIX_2_COMMANDS: ComponentMatrix2Commands = {
  ...createComponentMatrix2RenderingCommands(action =>
    dispatchAction(action, VIEW_ID)
  ),
  onComponentClick: noop,
  onComponentDoubleClick: setContextComponentId,
  ...createComponentMatrix2ResizeCommands(updatePersistentViewSettings),
  updateViewContext,
  updateViewProperties,
  updateViewLegendSettings,
};

export const PRESENTATION_MODE_COMPONENT_MATRIX_2_COMMANDS: ComponentMatrix2Commands =
  {
    ...COMPONENT_MATRIX_2_COMMANDS,
    // The handler ignores ids that aren't components, so list-value header ids
    // (dispatched from the row/column header cells) are safe no-ops.
    onItemSelect: itemId => {
      COMPONENT_MATRIX_2_COMMANDS.onItemSelect(itemId);
      if (itemId) {
        dispatchAction(componentSelectedInPresentationMode({ id: itemId }));
      }
    },
  };

export const NOOP_COMPONENT_MATRIX_2_DATA_LAYER_COMMANDS: ComponentMatrix2DataLayerCommands =
  {
    onComponentClick: noop,
    onComponentDoubleClick: noop,
    updateViewContext: noop,
    updateViewProperties: noop,
    updateViewLegendSettings: noop,
    onHoveredComponentChange: noop,
    onFocusedComponentChange: noop,
    updateViewSettings: noop,
    updateViewComponentIds: noop,
  };
